import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { generateImageReportPDF } from './ImageReportPDF';
import { showSnackBar } from 'utils/Global';
import { IStyle } from 'Styles';
import { LoadingModal } from 'components/modal';
import {
  useAllWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { useExperimentReady } from './hooks';

type CaptureProfileOption = {
  id: number;
  name?: string;
  selected: boolean;
};

type ImageReportOptionProps = {
  docData: ExperimentDtoClient;
  canvasRef: any;
  platePadding: number;
};

export type ImageReportOptionsDTO = {
  dropNumber: string;
  inspection: number;
  fitToPage: boolean;
  rowLimit: number;
  columnLimit: number;
};

export type ImageReportTextProps = {
  placeHolder: any;
  style?: any;
  onChange: any;
  disabled: any;
  value: any;
  inputClass?: string;
};

export const ImageReportTextInput = React.forwardRef<
  HTMLInputElement,
  ImageReportTextProps
>(({ style, placeHolder, onChange, value, disabled, inputClass }, ref) => {
  return (
    <input
      id={`imagereportoptions-textinput-${placeHolder}`}
      ref={ref}
      value={value}
      type="number"
      placeholder={placeHolder}
      onChange={onChange}
      disabled={disabled}
      style={{
        ...style,
      }}
      className={inputClass}
    />
  );
});

export const ImageReportOptions = forwardRef((props: ImageReportOptionProps, ref) => {
  const allWellNavigationDetails = useAllWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const populatingWellList = useExperimentReady(props.docData);
  const latestInspectionId =
    props.docData.inspections?.[props.docData.inspections.length - 1]?.id || 0;
  const currentInspectionId =
    selectedDropImageParameters?.inspection?.id || latestInspectionId;

  const [dropNumber, setDropNumber] = useState('currentDrop');
  const [inspection, setInspection] = useState(currentInspectionId);
  const [fitToPage, setFitToPage] = useState(true);
  const [rowLimit, setRowLimit] = useState(1);
  const [columnLimit, setColumnLimit] = useState(1);
  const [captureProfiles, setCaptureProfiles] = useState<CaptureProfileOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  let rowLimitOption: any;
  let columnLimitOption: any;
  const rowValueRef = useRef(null);
  const columnValueRef = useRef(null);

  useEffect(() => {
    props.docData.profiles.forEach((profile, i) => {
      captureProfiles.push({ ...profile, selected: i == 0 });
    });
    setCaptureProfiles([...captureProfiles]);
  }, []);

  useEffect(() => {
    if (fitToPage === true) {
      setRowLimit(props.docData.containerRows);
      setColumnLimit(props.docData.containerCols);
      rowValueRef.current.value = null;
      columnValueRef.current.value = null;
    }
  }, [fitToPage]);

  useImperativeHandle(ref, () => ({
    generateAction,
  }));

  async function generateAction(saveReport: boolean, onPopupBlocked?: () => void) {
    if (
      rowLimit <= 0 ||
      rowLimit > props.docData.containerRows ||
      (rowLimit === null && fitToPage !== true) ||
      columnLimit <= 0 ||
      columnLimit > props.docData.containerCols ||
      (columnLimit === null && fitToPage !== true)
    ) {
      showSnackBar('Please enter valid number of rows and columns.');
      return false;
    } else {
      setIsLoading(true);
      const reportOptions: ImageReportOptionsDTO = {
        dropNumber,
        inspection,
        fitToPage,
        rowLimit,
        columnLimit,
      };
      const completeDocData = await populatingWellList;
      await generateImageReportPDF(
        reportOptions,
        completeDocData,
        allWellNavigationDetails,
        selectedDropImageParameters,
        saveReport,
        onPopupBlocked,
      );
      setIsLoading(false);
      return true;
    }
  }

  function setDropNumberValue(e) {
    setDropNumber(e.target.value);
  }

  function setInspectionSet(e) {
    setInspection(e.target.value);
  }

  function changeColumnValue(e) {
    if (!isNaN(Number(e.target.value))) {
      if (e.target.value === '') {
        setColumnLimit(null);
      } else {
        setColumnLimit(Number(e.target.value));
      }
    } else {
      setColumnLimit(null);
    }
  }
  function changeRowValue(e) {
    if (!isNaN(Number(e.target.value))) {
      if (e.target.value === '') {
        setRowLimit(null);
      } else {
        setRowLimit(Number(e.target.value));
      }
    } else {
      setRowLimit(null);
    }
  }

  return (
    <div
      id={`imagereportoptions-wrapper`}
      className="dashed-border d-flex h-100 p-2 flex-column"
      style={{ height: '40rem' }}
    >
      <div id={`imagereportoptions-message`} className="mb-2">
        Please provide information you want to include into report
      </div>

      <div id="imagereportoptions-selection-group" className="flex" style={styles.root}>
        <label id="imagereportoptions-label-inspection">Inspection</label>
        <div id="imagereportoptions-select-inspection" style={styles.combos}>
          <select
            name="inspections"
            id="imagereportoptions-dropdown-inspection"
            onChange={setInspectionSet}
            value={inspection}
          >
            {props.docData.inspections.map((inspection, index) => (
              <option
                id={`imagereportoptions-dropdown-inspection-options-${index}`}
                key={`ins_opt_${index}`}
                value={inspection.id}
              >
                {new Date(inspection.dateImaged).toLocaleTimeString() +
                  ' ' +
                  new Date(inspection.dateImaged).toLocaleDateString('en-US')}
              </option>
            ))}
          </select>
        </div>

        <label id="imagereportoptions-label-drop">Drop</label>
        <div id="imagereportoptions-select-drop" style={styles.combos}>
          <select
            name="dropNumber"
            id="imagereportoptions-dropdown-dropnumber"
            onChange={setDropNumberValue}
            value={dropNumber}
          >
            <option
              id={`imagereportoptions-dropdown-dropNumber-currentDrop`}
              key={`dropNumber-currentDrop`}
              value={'currentDrop'}
            >
              Current Drop
            </option>
            <option
              id={`imagereportoptions-dropdown-dropNumber-allDrops`}
              key={`dropNumber-allDrops`}
              value={'allDrops'}
            >
              All Drops
            </option>
            {props.docData.containerDrops.map((drop) => (
              <option
                id={`imagereportoptions-dropdown-dropNumber-drop-${drop.dropNumber.toString()}`}
                key={`dropNumber-${drop.dropNumber.toString()}`}
                value={drop.dropNumber.toString()}
              >
                Drop {drop.dropNumber}
              </option>
            ))}
          </select>
        </div>
        <ReportOption
          input={
            <RMCheckBoxY
              isChecked={fitToPage}
              onChange={(e) => {
                setFitToPage(e);
              }}
            />
          }
          label="Fit to Page"
          id={`imagereportoptions-checkbox-fittopage`}
        />
      </div>

      <div id="imagereportoptions-dimension-group" className="flex" style={styles.root}>
        <label id="imagereportoptions-label-rows" style={{ paddingRight: '1%' }}>
          Rows
        </label>
        <ImageReportTextInput
          ref={rowValueRef}
          placeHolder={''}
          onChange={changeRowValue}
          value={rowLimitOption}
          disabled={fitToPage}
          style={{ height: '2.00rem', width: '10%', borderRadius: '3px' }}
        />
        <label
          id="imagereportoptions-label-columns"
          style={{ paddingRight: '1%', paddingLeft: '1%' }}
        >
          Columns
        </label>

        <ImageReportTextInput
          ref={columnValueRef}
          placeHolder={''}
          onChange={changeColumnValue}
          value={columnLimitOption}
          disabled={fitToPage}
          style={{ height: '2.00rem', width: '10%', borderRadius: '3px' }}
        />
      </div>

      <LoadingModal showModal={isLoading} text={'Preparing'} />
    </div>
  );
});

const ReportOption = ({ input, label }: any) => {
  return (
    <div
      id={`imagereportoptions-reportoption-wrapper`}
      className="d-flex flex-row align-items-baseline"
    >
      <div id={`imagereportoptions-reportoption-${input}`}>{input}</div>
      <label
        id={`imagereportoptions-reportoption-${label}`}
        className="flex-90 non-select"
      >
        {' '}
        {label}{' '}
      </label>
    </div>
  );
};

const styles: IStyle = {
  root: {
    alignItems: 'baseline',
  },
  combos: {
    paddingLeft: '1%',
    paddingRight: '3%',
  },
};
